import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { WidgetLanguage } from '../types';
import { TRANSLATIONS } from './translations';

const DEFAULT_LANGUAGE: WidgetLanguage = 'en';
const SUPPORTED_LANGUAGES: readonly WidgetLanguage[] = ['en', 'es', 'cn', 'tw', 'ru', 'jp'];

/** A translator bound to a resolved locale: maps an English key to its localized string. */
export type Translator = (key: string) => string;

/**
 * Normalize a raw `lang` attribute (trim + lowercase) to a supported language.
 * Unknown, empty, whitespace, or missing values fall back to English (FR-1.1).
 */
export const resolveLanguage = (raw: string | null | undefined): WidgetLanguage => {
  const normalized = (raw ?? '').trim().toLowerCase();
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(normalized)
    ? (normalized as WidgetLanguage)
    : DEFAULT_LANGUAGE;
};

/**
 * Build a translator for a locale. Mirrors the legacy `translation.coffee` lookup:
 * English returns the key unchanged; other locales return the translation only when
 * the key exists and its value is non-empty, otherwise fall back to the English key
 * (FR-1.6) — never a blank or a raw miss.
 */
const createTranslator = (locale: WidgetLanguage): Translator => {
  if (locale === DEFAULT_LANGUAGE) {
    return key => key;
  }
  const dictionary = TRANSLATIONS[locale];
  return key => {
    const value = dictionary[key];
    return value && value.length > 0 ? value : key;
  };
};

interface I18nContextValue {
  locale: WidgetLanguage;
  t: Translator;
}

const I18nContext = createContext<I18nContextValue>({
  locale: DEFAULT_LANGUAGE,
  t: key => key,
});

interface I18nProviderProps {
  locale: WidgetLanguage;
  children: ReactNode;
}

export const I18nProvider = ({ locale, children }: I18nProviderProps): ReactNode => {
  const value = useMemo<I18nContextValue>(
    () => ({ locale, t: createTranslator(locale) }),
    [locale],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

/** Access the active locale and translator from the nearest {@link I18nProvider}. */
export const useTranslation = (): I18nContextValue => useContext(I18nContext);
